import { deserialize, serialize } from './serializer';
import { hash } from './hash';

export const enum MessageType {
  ExposeAPI = 'ExposeAPI',
  Call = 'Call',
  Response = 'Response',
  Cleanup = 'Cleanup',
}

export type IRegisterMessageFunctionMarker = (
  correlationId: string,
  path: string,
  value: (...args: any) => any,
) => void;

export type IReplaceMessageFunctionMarker = (
  correlationId: string,
  path: string,
) => (props: any[]) => any;

export const noopFunctionReplacer = () => () => {};

export class Message<
  TPayload extends Record<string, any> = Record<string, any>,
> {
  public readonly correlationId = `M-${hash()}`;
  public parentCorrelationId?: string;

  constructor(
    public type: MessageType,
    public payload: TPayload,
  ) {}

  public withParentCorrelationId(correlationId: string) {
    this.parentCorrelationId = correlationId;
    return this;
  }

  public serialize(registerFunctionMarker: IRegisterMessageFunctionMarker) {
    return JSON.stringify({
      parentCorrelationId: this.parentCorrelationId,
      correlationId: this.correlationId,
      type: this.type,
      payload: serialize(this.payload, (path, value) => {
        registerFunctionMarker(this.correlationId, path, value);
      }),
    });
  }

  static fromEvent(
    data: string,
    replaceFunctionMarker: IReplaceMessageFunctionMarker,
  ) {
    const parsed = JSON.parse(data);

    return new Message(
      parsed.type,
      deserialize(parsed.payload, (breadcrumbs) => {
        return replaceFunctionMarker(parsed.correlationId, breadcrumbs);
      }),
    ).withParentCorrelationId(parsed.correlationId);
  }
}

export class MessageCleanup extends Message<{
  functionMarkerKey: string;
  callId?: string;
}> {
  constructor(payload: typeof MessageCleanup.prototype.payload) {
    super(MessageType.Cleanup, payload);
  }
}

export class MessageResponse extends Message<{
  callId: string;
  response: any;
}> {
  constructor(payload: typeof MessageResponse.prototype.payload) {
    super(MessageType.Response, payload);
  }
}

export class MessageResponseError extends Message<{
  callId: string;
  errorMessage: string;
  errorStack?: string;
}> {
  constructor(payload: typeof MessageResponseError.prototype.payload) {
    super(MessageType.Response, payload);
  }
}

export class MessageCall extends Message<{
  callId: string;
  functionMarkerKey: string;
  props: any[];
}> {
  constructor(payload: typeof MessageCall.prototype.payload) {
    super(MessageType.Call, payload);
  }
}

export class MessageExposeAPI extends Message<
  {
    type: string;
  } & Record<string, any>
> {
  constructor(payload: typeof MessageExposeAPI.prototype.payload) {
    super(MessageType.ExposeAPI, payload);
  }
}
